import '../menu/menu.scss';

import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { usePopper } from 'react-popper';
import classNames from 'classnames';

import { useOutsideMouseDown, useOutsideScroll, useDelayedUnmount } from '../hooks';
import { MoreSVGIcon } from '../svg'
import { CommentState } from './commentState';

type Props = {
  model: CommentState,
  className?: string,
  onOpen?: (isOpened: boolean) => void
};

export const CommentMenu = observer((props: Props) => {
  const { model, onOpen } = props;
  const { comment } = model;

  const [anchorElement, setAnchorElement] = useState<HTMLButtonElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const [isOpened, setIsOpened] = useState(false);
  const [forceClose, setForceClose] = useState(false);
  const { shouldRender, isAnimating } = useDelayedUnmount(isOpened && !forceClose, forceClose ? 0 : 400);

  // Listen for custom event to close this menu when another opens
  useEffect(() => {
    const handleCloseAll = () => {
      if (isOpened) {
        setForceClose(true);
        setIsOpened(false);
      }
    };
    document.addEventListener('dropdown:close-all', handleCloseAll);
    return () => document.removeEventListener('dropdown:close-all', handleCloseAll);
  }, [isOpened]);

  // Reset forceClose when menu is fully closed
  useEffect(() => {
    if (!isOpened && forceClose) {
      setForceClose(false);
    }
  }, [isOpened, forceClose]);

  const className = classNames('comment-menu', props?.className, {
    'active': isOpened
  });

  const { styles, attributes } = usePopper(anchorElement, popperElement, {
    placement: 'bottom-end',
    strategy: 'fixed',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 4],
        }
      },
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['bottom', 'bottom-start'],
        },
      }
    ],
  });

  useOutsideMouseDown(popperElement, () => setIsOpened(false));
  useOutsideScroll(popperElement, () => setIsOpened(false));

  useEffect(() => {
    if (onOpen) onOpen(isOpened);
  }, [onOpen, isOpened]);

  const canEdit = comment.hasPermission('EditComment');
  const canDelete = comment.hasPermission('DeleteComment');

  if (!canEdit && !canDelete)
    return null;

  return (
    <>
      <button className={className}
        ref={setAnchorElement as React.RefCallback<HTMLButtonElement>}
        onClick={evt => {
          if (!isOpened) {
            document.dispatchEvent(new CustomEvent('dropdown:close-all'));
          }
          setIsOpened(!isOpened);
          evt.stopPropagation();
        }}>
        <span className="icon">
          <MoreSVGIcon />
        </span>
      </button>

      {shouldRender && (
        <div className={classNames('comment-menu-popup menu-animate', isAnimating)}
          ref={setPopperElement as React.RefCallback<HTMLDivElement>}
          style={styles.popper}
          {...attributes.popper}>
          <div className="menu-items"
            onClick={() => setIsOpened(false)}>

            {canDelete && (
              <button className="menu-item"
                onClick={evt => {
                  model.openDeleteWindow();
                  setIsOpened(false);
                  evt.stopPropagation();
                }}>
                Delete
              </button>
            )}

            {canEdit && (
              <button className="menu-item"
                onClick={evt => {
                  model.toggleEditInput();
                  setIsOpened(false);
                  evt.stopPropagation();
                }}>
                Edit
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
});